import React, { useState, useEffect } from 'react';
import { createMyVideo, completeMyVideo, putToSignedUrl } from '../services/api';
import { probeDuration, captureVideoPoster } from '../utils/media';

const inputStyle = {
  background: 'var(--bg-input)',
  border: '1px solid var(--border-accent)',
  color: 'var(--text-primary)',
  outline: 'none',
};

const TYPE_BY_EXT = { mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm' };

// Some browsers report '' for .mov — fall back to the extension.
function videoType(file) {
  if (file.type) return file.type;
  return TYPE_BY_EXT[file.name.split('.').pop().toLowerCase()] || '';
}

/**
 * UploadMyVideoModal — any member uploads their own speech video.
 *
 * The file goes straight from the browser to Stratus on a signed URL; it never
 * passes through our function. After the upload lands, the row moves to
 * 'processing' and the transcription cron picks it up. The video appears in the
 * Library at once, locked to everyone who isn't the uploader's friend.
 *
 * Props:
 *   onUploaded {fn} (speechId) => void
 *   onClose    {fn} () => void — ignored while an upload is running
 */
export default function UploadMyVideoModal({ onUploaded, onClose }) {
  const [title, setTitle] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null); // null = not uploading
  const [error, setError] = useState(null);
  const busy = progress !== null;

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, busy]);

  // Leaving mid-upload abandons it; warn rather than lose a large transfer silently.
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const type = file && videoType(file);
    if (!file || !type) {
      setError('Pick an MP4, MOV or WebM video.');
      return;
    }

    setProgress(0);
    try {
      const [duration, poster] = await Promise.all([probeDuration(file), captureVideoPoster(file)]);
      const created = await createMyVideo({
        title: title.trim(),
        synopsis: synopsis.trim() || undefined,
        mime_type: type,
        duration_secs: Math.round(duration),
      });

      await putToSignedUrl(created.upload_url, file, { contentType: type, onProgress: setProgress });
      if (poster) {
        // Best-effort: a missing poster only means a placeholder card.
        await putToSignedUrl(created.poster_upload_url, poster, { contentType: 'image/jpeg' }).catch(() => {});
      }
      await completeMyVideo(created.speech_id);
      onUploaded(created.speech_id);
    } catch (err) {
      setProgress(null);
      setError(errorMessage(err));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
      >
        <div>
          <h3 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Upload your video
          </h3>
          <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-faint)' }}>
            Listed in the Library for everyone. Only your friends can watch it.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="my-video-file" className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              Video <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <input
              id="my-video-file"
              type="file"
              accept="video/mp4,video/quicktime,video/webm,.mov,.mp4,.webm"
              disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm"
              style={{ color: 'var(--text-secondary)' }}
            />
            {file && (
              <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
                {file.name} · {(file.size / 1048576).toFixed(0)} MB
              </p>
            )}
          </div>

          <div>
            <label htmlFor="my-video-title" className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              Title <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <input
              id="my-video-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={300}
              required
              disabled={busy}
              placeholder="e.g. Designing for Trust"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="my-video-synopsis" className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              Description
            </label>
            <textarea
              id="my-video-synopsis"
              value={synopsis}
              onChange={(e) => setSynopsis(e.target.value)}
              maxLength={1000}
              rows={3}
              disabled={busy}
              placeholder="A sentence or two about the talk (optional)"
              className="w-full px-3 py-2 rounded-lg text-sm resize-none"
              style={inputStyle}
            />
          </div>

          {busy && (
            <div role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                <div className="h-full transition-all" style={{ width: `${progress}%`, background: 'var(--primary)' }} />
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
                Uploading… {progress}% — keep this tab open
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm" style={{ color: 'var(--color-danger)' }}>⚠ {error}</p>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} disabled={busy} className="pill-tab text-sm" style={{ color: 'var(--text-muted)' }}>
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-bouncy text-sm px-5 py-2">
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function errorMessage(err) {
  switch (err.code) {
    case 'INVALID_FILE_TYPE':   return 'Upload an MP4, MOV or WebM video.';
    case 'FILE_TOO_LONG':       return 'Videos can be at most 2 hours long.';
    case 'MISSING_FIELDS':      return 'Give your video a title.';
    case 'RATE_LIMIT_EXCEEDED': return 'You can upload 5 videos a day. Try again tomorrow.';
    case 'UPLOAD_FAILED':       return 'The upload did not go through. Check your connection and try again.';
    default:                    return err.message || 'Upload failed';
  }
}
